import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';

import { RoutesEnum } from '@/types/routes.types';

type WindowManagerContextType = {
  focusOrOpenWindow: (screenId: 1 | 2) => void;
};

export const WindowManagerContext = createContext<WindowManagerContextType | null>(null);

export const WindowManagerContextProvider = ({ children }: Readonly<PropsWithChildren>) => {
  const screen1Ref = useRef<Window | null>(null);
  const screen2Ref = useRef<Window | null>(null);

  const focusOrOpenWindow = useCallback((screenId: 1 | 2) => {
    const ref = screenId === 1 ? screen1Ref : screen2Ref;

    if (ref.current && !ref.current.closed) {
      ref.current.focus();
    } else {
      ref.current = window.open(`${RoutesEnum.SCREEN}/${screenId}`, `screen-${screenId}`, 'popup');
    }
  }, []);

  const values = useMemo(() => ({ focusOrOpenWindow }), [focusOrOpenWindow]);

  return <WindowManagerContext value={values}>{children}</WindowManagerContext>;
};

export function useWindowManager() {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useWindowManager must be used inside WindowManagerProvider');
  }
  return context;
}
